import React, { useEffect } from 'react';
import { Form, Input, Button, InputNumber, DatePicker, Select, Row, Col, message } from 'antd';
import { useExpense } from '../../store/expenseContext';
import { useCurrency } from '../../store/currencyContext';
import dayjs from 'dayjs';
import { Expense } from '../../types/expense';
import { useTranslation } from "react-i18next";

const PAYMENT_METHODS = [
    { value: "cash", label: "Cash" },
    { value: "bank", label: "Bank" },
    { value: "other", label: "Other" },
];

// You may want to fetch or hardcode categories
const CATEGORIES = [
    { value: "rent", label: "Rent" },
    { value: "salary", label: "Salary" },
    { value: "utilities", label: "Utilities" },
    { value: "office", label: "Office" },
    { value: "other", label: "Other" },
];

interface Props {
    editExpense?: Expense;
    onFinishEdit?: () => void;
}

const ExpenseForm: React.FC<Props> = ({ editExpense, onFinishEdit }) => {
    const [form] = Form.useForm();
    const { addExpense, updateExpense } = useExpense();
    const { currencies } = useCurrency();
    const { t } = useTranslation();

    const currencyList = currencies.map(c => ({
        value: c.code,
        label: c.symbol ? `${c.code} (${c.symbol})` : c.code
    }));

    useEffect(() => {
        const DEFAULT_CURRENCY = currencies.find(c => c.isDefault)?.code || "IQD";
        if (editExpense) {
            form.setFieldsValue({
                ...editExpense,
                date: editExpense.date ? dayjs(editExpense.date) : dayjs()
            });
        } else {
            form.setFieldsValue({ date: dayjs(), amount: 0, currency: DEFAULT_CURRENCY });
        }
        // eslint-disable-next-line
    }, [editExpense, currencies]);

    const onFinish = (values: any) => {
        if (!values.amount || values.amount <= 0) {
            message.error(t('enter_valid_amount'));
            return;
        }
        const formatted = {
            ...values,
            date: values.date ? values.date.toISOString() : undefined,
        };
        if (editExpense) {
            updateExpense({ ...editExpense, ...formatted });
            onFinishEdit?.();
        } else {
            addExpense(formatted);
            form.resetFields();
            const DEFAULT_CURRENCY = currencies.find(c => c.isDefault)?.code || "IQD";
            form.setFieldsValue({ currency: DEFAULT_CURRENCY });
        }
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            style={{ maxWidth: 600, margin: '0 auto', background: "#fff", padding: 24, borderRadius: 10 }}
            autoComplete="off"
        >
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item name="expenseNumber" label={t('expenseNumber')} rules={[{ required: true, message: t('required') as string }]}>
                        <Input placeholder={t('expense_number') as string} />
                    </Form.Item>
                    <Form.Item name="date" label={t('date')} rules={[{ required: true, message: t('required') as string }]}>
                        <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item name="category" label={t('category')} rules={[{ required: true, message: t('required') as string }]}>
                        <Select options={CATEGORIES} placeholder={t('category')} />
                    </Form.Item>
                    <Form.Item name="paidTo" label={t('paidTo')}>
                        <Input placeholder={t('paid_to') as string} />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="amount" label={t('amount')} rules={[{ required: true, message: t('required') as string }]}>
                        <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item name="currency" label={t('currency')} rules={[{ required: true, message: t('required') as string }]}>
                        <Select
                            options={currencyList}
                            placeholder={t('currency')}
                            showSearch
                            optionFilterProp="label"
                        />
                    </Form.Item>
                    <Form.Item name="paymentMethod" label={t('paymentMethod')}>
                        <Select options={PAYMENT_METHODS} placeholder={t('payment_method')} allowClear />
                    </Form.Item>
                    <Form.Item name="reference" label={t('reference')}>
                        <Input placeholder={t('reference_number') as string} />
                    </Form.Item>
                    <Form.Item name="description" label={t('description')}>
                        <Input.TextArea placeholder={t('description') as string} autoSize />
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item>
                <Button type="primary" htmlType="submit" block>
                    {editExpense ? t('update_expense') : t('create_expense')}
                </Button>
            </Form.Item>
        </Form>
    );
};

export default ExpenseForm;
